import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type MarketCategory = 'crypto' | 'forex' | 'stocks' | 'commodities'

export interface MarketInstrument {
  symbol: string
  tvSymbol: string
  /** Backend `instruments.id` for `/client/positions` — required to place trades. */
  instrumentId?: number
  /** Short ticker shown in list (e.g. AAVEUSD.) */
  ticker: string
  /** Display label for chart header */
  label: string
  /** Subtitle e.g. "AAVE vs USD" */
  name: string
  category: MarketCategory
  bid: number
  ask: number
  open: number
  high: number
  last: number
  changePct: number
  volume: number
  /** Simple icon key for avatar */
  icon: string
  currency: string
}

export const MARKET_INSTRUMENTS: MarketInstrument[] = [
  {
    symbol: 'AAVEUSD',
    tvSymbol: 'BINANCE:AAVEUSDT',
    ticker: 'AAVEUSD.',
    label: 'AAVE/USD',
    name: 'AAVE vs USD',
    category: 'crypto',
    bid: 146.4191,
    ask: 146.6854,
    open: 145.78,
    high: 154.57,
    last: 146.55,
    changePct: -0.0436,
    volume: 26,
    icon: 'aave',
    currency: 'USD',
  },
  {
    symbol: 'AAVEUSDT',
    tvSymbol: 'BINANCE:AAVEUSDT',
    ticker: 'AAVEUSDT.',
    label: 'AAVE/USDT',
    name: 'AAVE vs TetherUS',
    category: 'crypto',
    bid: 146.45,
    ask: 146.72,
    open: 145.8,
    high: 154.6,
    last: 146.58,
    changePct: -0.0436,
    volume: 26,
    icon: 'aave',
    currency: 'USDT',
  },
  {
    symbol: 'ADABTC',
    tvSymbol: 'BINANCE:ADABTC',
    ticker: 'ADABTC.',
    label: 'ADA/BTC',
    name: 'Cardano vs Bitcoin',
    category: 'crypto',
    bid: 0.00000407,
    ask: 0.00000409,
    open: 0.00000405,
    high: 0.00000418,
    last: 0.00000408,
    changePct: -0.0097,
    volume: 16,
    icon: 'ada',
    currency: 'BTC',
  },
  {
    symbol: 'ADAETH',
    tvSymbol: 'BINANCE:ADAETH',
    ticker: 'ADAETH.',
    label: 'ADA/ETH',
    name: 'Cardano vs Ethereum',
    category: 'crypto',
    bid: 0.0001431,
    ask: 0.0001438,
    open: 0.000142,
    high: 0.000148,
    last: 0.0001434,
    changePct: -0.0121,
    volume: 14,
    icon: 'ada',
    currency: 'ETH',
  },
  {
    symbol: 'ADAUSD',
    tvSymbol: 'BINANCE:ADAUSDT',
    ticker: 'ADAUSD.',
    label: 'ADA/USD',
    name: 'Cardano vs USD',
    category: 'crypto',
    bid: 0.3441,
    ask: 0.3446,
    open: 0.34,
    high: 0.352,
    last: 0.3443,
    changePct: -0.0152,
    volume: 23,
    icon: 'ada',
    currency: 'USD',
  },
  {
    symbol: 'ADAUSDT',
    tvSymbol: 'BINANCE:ADAUSDT',
    ticker: 'ADAUSDT.',
    label: 'ADA/USDT',
    name: 'Cardano vs TetherUS',
    category: 'crypto',
    bid: 0.3442,
    ask: 0.3447,
    open: 0.3401,
    high: 0.3521,
    last: 0.3444,
    changePct: -0.0152,
    volume: 23,
    icon: 'ada',
    currency: 'USDT',
  },
  {
    symbol: 'ALGOBTC',
    tvSymbol: 'BINANCE:ALGOBTC',
    ticker: 'ALGOBTC.',
    label: 'ALGO/BTC',
    name: 'Algorand vs Bitcoin',
    category: 'crypto',
    bid: 0.00000128,
    ask: 0.00000129,
    open: 0.00000125,
    high: 0.00000132,
    last: 0.000001285,
    changePct: 0.0231,
    volume: 10,
    icon: 'algo',
    currency: 'BTC',
  },
  {
    symbol: 'ALGOETH',
    tvSymbol: 'BINANCE:ALGOETH',
    ticker: 'ALGOETH.',
    label: 'ALGO/ETH',
    name: 'Algorand vs Ethereum',
    category: 'crypto',
    bid: 0.00004492,
    ask: 0.0000452,
    open: 0.000044,
    high: 0.0000465,
    last: 0.000045,
    changePct: 0.0133,
    volume: 9,
    icon: 'algo',
    currency: 'ETH',
  },
  {
    symbol: 'ALGOUSD',
    tvSymbol: 'BINANCE:ALGOUSDT',
    ticker: 'ALGOUSD.',
    label: 'ALGO/USD',
    name: 'Algorand vs USD',
    category: 'crypto',
    bid: 0.1081,
    ask: 0.1084,
    open: 0.106,
    high: 0.111,
    last: 0.1082,
    changePct: 0.0102,
    volume: 18,
    icon: 'algo',
    currency: 'USD',
  },
  {
    symbol: 'ALGOUSDT',
    tvSymbol: 'BINANCE:ALGOUSDT',
    ticker: 'ALGOUSDT.',
    label: 'ALGO/USDT',
    name: 'Algorand vs TetherUS',
    category: 'crypto',
    bid: 0.10815,
    ask: 0.10845,
    open: 0.1061,
    high: 0.1112,
    last: 0.1083,
    changePct: 0.0102,
    volume: 18,
    icon: 'algo',
    currency: 'USDT',
  },
  {
    symbol: 'BTCUSDT',
    tvSymbol: 'BINANCE:BTCUSDT',
    ticker: 'BTCUSDT.',
    label: 'BTC/USDT',
    name: 'Bitcoin vs TetherUS',
    category: 'crypto',
    bid: 67210.2,
    ask: 67241.5,
    open: 66400,
    high: 67890,
    last: 67241.5,
    changePct: 0.0142,
    volume: 142,
    icon: 'btc',
    currency: 'USDT',
  },
  {
    symbol: 'ETHUSDT',
    tvSymbol: 'BINANCE:ETHUSDT',
    ticker: 'ETHUSDT.',
    label: 'ETH/USDT',
    name: 'Ethereum vs TetherUS',
    category: 'crypto',
    bid: 3419.5,
    ask: 3421.8,
    open: 3440,
    high: 3488,
    last: 3421.8,
    changePct: -0.0061,
    volume: 98,
    icon: 'eth',
    currency: 'USDT',
  },
  {
    symbol: 'EURUSD',
    tvSymbol: 'FX:EURUSD',
    instrumentId: 1,
    ticker: 'EURUSD.',
    label: 'EUR/USD',
    name: 'Euro vs US Dollar',
    category: 'forex',
    bid: 1.084,
    ask: 1.0842,
    open: 1.082,
    high: 1.0865,
    last: 1.0842,
    changePct: 0.0018,
    volume: 54,
    icon: 'eu',
    currency: 'USD',
  },
  {
    symbol: 'GBPUSD',
    tvSymbol: 'FX:GBPUSD',
    ticker: 'GBPUSD.',
    label: 'GBP/USD',
    name: 'Pound vs US Dollar',
    category: 'forex',
    bid: 1.3012,
    ask: 1.3015,
    open: 1.298,
    high: 1.305,
    last: 1.3014,
    changePct: 0.0024,
    volume: 41,
    icon: 'gb',
    currency: 'USD',
  },
  {
    symbol: 'AAPL',
    tvSymbol: 'NASDAQ:AAPL',
    ticker: 'AAPL.',
    label: 'AAPL',
    name: 'Apple Inc.',
    category: 'stocks',
    bid: 228.35,
    ask: 228.41,
    open: 225.1,
    high: 229.8,
    last: 228.41,
    changePct: 0.0124,
    volume: 67,
    icon: 'aapl',
    currency: 'USD',
  },
  {
    symbol: 'XAUUSD',
    tvSymbol: 'OANDA:XAUUSD',
    ticker: 'XAUUSD.',
    label: 'XAU/USD',
    name: 'Gold vs US Dollar',
    category: 'commodities',
    bid: 2653.8,
    ask: 2654.3,
    open: 2638,
    high: 2662,
    last: 2654.3,
    changePct: 0.0061,
    volume: 33,
    icon: 'xau',
    currency: 'USD',
  },
  {
    symbol: 'SOLUSDT',
    tvSymbol: 'BINANCE:SOLUSDT',
    ticker: 'SOLUSDT.',
    label: 'SOL/USDT',
    name: 'Solana vs TetherUS',
    category: 'crypto',
    bid: 178.2,
    ask: 178.42,
    open: 173.5,
    high: 181.2,
    last: 178.42,
    changePct: 0.0284,
    volume: 55,
    icon: 'sol',
    currency: 'USDT',
  },
]

interface MarketContextValue {
  instruments: MarketInstrument[]
  active: MarketInstrument
  favorites: Set<string>
  toggleFavorite: (symbol: string) => void
  setSymbol: (symbol: string) => void
}

const MarketContext = createContext<MarketContextValue | null>(null)

const FAV_KEY = 'velox.market_favorites'

function readFavorites(): Set<string> {
  try {
    const raw = localStorage.getItem(FAV_KEY)
    if (!raw) return new Set()
    const arr = JSON.parse(raw) as string[]
    return new Set(Array.isArray(arr) ? arr : [])
  } catch {
    return new Set()
  }
}

export function MarketProvider({ children }: { children: ReactNode }) {
  const [symbol, setSymbolState] = useState(MARKET_INSTRUMENTS[0].symbol)
  const [favorites, setFavorites] = useState<Set<string>>(readFavorites)

  const setSymbol = useCallback((next: string) => {
    setSymbolState(next)
  }, [])

  const toggleFavorite = useCallback((sym: string) => {
    setFavorites((prev) => {
      const next = new Set(prev)
      if (next.has(sym)) next.delete(sym)
      else next.add(sym)
      try {
        localStorage.setItem(FAV_KEY, JSON.stringify([...next]))
      } catch {
        /* ignore */
      }
      return next
    })
  }, [])

  const active =
    MARKET_INSTRUMENTS.find((i) => i.symbol === symbol) ?? MARKET_INSTRUMENTS[0]

  const value = useMemo(
    () => ({
      instruments: MARKET_INSTRUMENTS,
      active,
      favorites,
      toggleFavorite,
      setSymbol,
    }),
    [active, favorites, toggleFavorite, setSymbol],
  )

  return (
    <MarketContext.Provider value={value}>{children}</MarketContext.Provider>
  )
}

export function useMarket(): MarketContextValue {
  const ctx = useContext(MarketContext)
  if (!ctx) {
    throw new Error('useMarket must be used within MarketProvider')
  }
  return ctx
}
